const TIME_PATTERN = /^(\d{1,2})(?:[:.](\d{1,2}))?/;

type AmPm = "AM" | "PM";

export interface ParsedTimeOfDay {
  hours: number;
  minutes: number;
  amPm: AmPm;
}

export const parseTimeOfDay = (
  input: string,
  fallbackAmPm: AmPm = "AM"
): ParsedTimeOfDay | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const match = trimmed.match(TIME_PATTERN);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;

  const remainder = trimmed.slice(match[0].length).toLowerCase();
  let detectedAmPm: AmPm | null = null;

  if (remainder.trim().startsWith("p")) {
    detectedAmPm = "PM";
  } else if (remainder.trim().startsWith("a")) {
    detectedAmPm = "AM";
  }

  if (hours >= 13 && hours <= 23) {
    hours -= 12;
    detectedAmPm = "PM";
  } else if (hours === 0) {
    hours = 12;
    detectedAmPm = detectedAmPm ?? "AM";
  }

  if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
    return null;
  }

  return {
    hours,
    minutes,
    amPm: detectedAmPm ?? fallbackAmPm,
  };
};

export const formatTimeOfDay = (parsed: ParsedTimeOfDay): string => {
  const paddedMinutes = String(parsed.minutes).padStart(2, "0");
  return `${parsed.hours}:${paddedMinutes} ${parsed.amPm}`;
};

export const applyTimeOfDayToDate = (
  date: Date,
  parsed: ParsedTimeOfDay
): Date => {
  const result = new Date(date);

  let hours24 = parsed.hours;

  if (parsed.amPm === "AM") {
    if (parsed.hours === 12) {
      hours24 = 0;
    }
  } else if (parsed.hours !== 12) {
    hours24 += 12;
  }

  result.setHours(hours24, parsed.minutes, 0, 0);
  return result;
};

export const getAmPmFromDate = (date: Date): AmPm => {
  return date.getHours() < 12 ? "AM" : "PM";
};
